import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, vModelText as _vModelText, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, resolveDynamicComponent as _resolveDynamicComponent, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "h-full relative" }
const _hoisted_2 = { class: "flex pointer-events-auto bg-gray-50" }
const _hoisted_3 = { class: "flex justify-between group gap-1relative" }
const _hoisted_4 = { class: "text-sm" }
const _hoisted_5 = { class: "overflow-hidden min-w-2 text-ellipsis" }
const _hoisted_6 = { class: "uppercase whitespace-nowrap" }
const _hoisted_7 = { class: "text-xs" }
const _hoisted_8 = ["id"]
const _hoisted_9 = ["onClick", "tabindex", "id"]
const _hoisted_10 = { class: "text-gray-400 hover:text-yellow-500" }
const _hoisted_11 = {
  key: 1,
  class: "inline-flex gap-1"
}
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { key: 0 }
const _hoisted_14 = ["onUpdate:modelValue", "onInput"]
const _hoisted_15 = { key: 0 }
const _hoisted_16 = ["onUpdate:modelValue", "placeholder", "onInput"]
const _hoisted_17 = ["onClick"]
const _hoisted_18 = /*#__PURE__*/_createElementVNode("div", { class: "h-[45px]" }, null, -1)
const _hoisted_19 = ["onClick", "onDblclick", "id"]
const _hoisted_20 = { class: "flex h-full items-center" }
const _hoisted_21 = { class: "text-xs font-medium text-gray-900 flex items-center justify-between w-full overflow-hidden truncate" }
const _hoisted_22 = {
  key: 2,
  class: "overflow-hidden text-ellipsis"
}
const _hoisted_23 = {
  key: 0,
  class: "px-3 py-2 border-t w-full border-solid border-t-gray-200 group-hover:bg-yellow-200 font-semibold"
}
const _hoisted_24 = { key: 0 }

import {
  ArrowDownIcon,
  ChevronDownIcon,
  FunnelIcon,
  LockClosedIcon,
} from "@heroicons/vue/24/outline";
import { FunnelIcon as FunnelIconSolid } from "@heroicons/vue/24/solid";
import VueDatePicker from "@vuepic/vue-datepicker";
import { OnClickOutside } from "@vueuse/components";
import { useElementSize } from "@vueuse/core";
import { format, getWeek, isDate } from "date-fns";
import debounce from "lodash.debounce";
import { computed, nextTick, onMounted, onUnmounted, PropType, Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import OaiTooltip from "shared/components/other/OaiTooltip.vue";
import { useTrackEvent } from "shared/composables/tracking";
import { HierarchyTagStore, HierarchyType } from "shared/types/HierarchyTag";
import { ProcessClass } from "shared/types/ProcessClass";
import { createIsMatchingLocationFn } from "shared/views/planner/hierarchyTags";
import TableResizeSlider from "@/components/other/TableResizeSlider.vue";
import LocationFilter from "@/components/process_filters/LocationFilter.vue";
import ProcessTypesFilter from "@/components/process_filters/ProcessTypesFilter.vue";
import { getProcessTypes, useProcessClasses } from "@/composables/process";
import { useHasProjectFeature } from "@/composables/project";
import { TableResizeSliderEvent } from "@/types/Tables";
import { Column, Data, Filter } from "@/views/process_table/types";

const sliderWidth = 20;
type SliderName = "process" | "location" | "date" | "duration" | "working_hours" | "full";
const minColumnWidth = 130;
type ColNames = "process" | "location" | "date" | "duration" | "working_hours";

export default /*#__PURE__*/_defineComponent({
  __name: 'Table',
  props: {
  columns: {
    type: Array as PropType<Column[]>,
    required: true,
  },
  data: {
    type: Array as PropType<Data[]>,
    required: true,
  },
  onDateFilter: {
    type: Function as PropType<(value: [Date, Date], data: Data[]) => Data[]>,
    required: false,
  },
  onRowClick: {
    type: Function as PropType<(row: Data) => void>,
    required: false,
  },
  onRowDbClick: {
    type: Function as PropType<(row: Data) => void>,
    required: false,
  },
  exportToExcel: {
    type: Function as PropType<() => void>,
    required: false,
  },
  sortedBy: {
    type: String,
    required: false,
  },
  summary: {
    type: Object as PropType<Record<string, string>>,
    required: false,
  },
  hierarchyTags: {
    type: Array as PropType<HierarchyTagStore[]>,
    required: true,
  },
  width: {
    type: Number,
    required: true,
  },
},
  emits: ["update:data"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emits = __emit;
const { locale, t } = useI18n();

const processClasses = useProcessClasses();

const data = ref(props.data);
const columns = ref(props.columns);
const filterSelectValues = ref({} as Record<string, { name: string; value: string }[]>);
const dateRange = ref(null);
const tableContent = ref(null as HTMLDivElement | null);
const router = useRouter();
const route = useRoute();

const lastSorted = ref(null as Column | null);
const tableIsScrolling = ref<boolean>(false);
const getContainerRef = () => tableContent;
const draggingSliderName = ref<SliderName | null>(null);
const isThisSliderDraggingOrNone = (sliderName: SliderName) =>
  draggingSliderName.value === null || draggingSliderName.value === sliderName;
// Usage
const { width: tableWidth } = useElementSize(
  tableContent,
  { width: 0, height: 0 },
  { box: "border-box" },
);

const colWidth = ref({
  process_ids: 0,
  process: 0,
  location: 0,
  date: 0,
  duration: 0,
  working_hours: 0,
} as Record<ColNames, number>);

const displayedData = computed(() => {
  return data.value?.slice(0, countPages.value * 100);
});

const detectTableScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  const scrollTop = target.scrollTop || 0;
  tableIsScrolling.value = scrollTop !== 0;
};

const renderCache: Map<CallableFunction, Map<unknown, unknown>> = new Map();
const processTypes = ref([]) as Ref<string[]>;
const tableInitialWidth = ref();

const extractFilters = (columns: Column[]) => {
  return columns.reduce((acc, column) => {
    if (!column.filterDisabled) {
      column.filters?.forEach((filter) => {
        acc[filter.type] =
          filter.filterValue && Object.keys(filter.filterValue).length > 0
            ? filter.filterValue
            : null;
      });
    }
    return acc;
  }, {} as Record<string, string | string[] | Record<string, string[]> | null | Date[]>);
};

const filters = ref(extractFilters(props.columns));

watch(
  () => props.columns,
  (value) => {
    columns.value = value;
  },
);

watch(
  () => props.data,
  (value) => {
    filters.value = extractFilters(columns.value);

    filterSelectValues.value = extractFilterSelectValues(value);

    processTypes.value = getProcessTypes(value, processClasses.value);
    data.value = value;

    if (props.sortedBy) {
      const column = columns.value.find((column) => column.field === props.sortedBy);
      if (column) {
        handleSort(column);
      }
    }
  },
);

watch(
  () => props.width,
  () => {
    colWidth.value = calculateColumnWidth(props.width);
  },
);
const initialLastCols = ref();

const handleWindowResize = () => {
  const column: Column | undefined = columns.value.find((col) => col.showFilter);

  if (column) {
    resizeFilterPopover(column);
  }
};

const resizeFilterPopover = (column: Column) => {
  const filterPopover = document.querySelector(
    `#filterContainer_${column.field} .PopoverPanelContent`,
  );
  const filterButton = document.getElementById(`filterButton_${column.field}`);
  if (!filterPopover || !filterButton) {
    return;
  }

  const popoverRect = filterPopover.getBoundingClientRect();
  const buttonRect = filterButton.getBoundingClientRect();
  const filterButtonCenter = buttonRect.left + buttonRect.width / 2;
  const windowWidth = window.innerWidth;

  // Center the popover by default
  let leftPosition = filterButtonCenter - popoverRect.width / 2;

  // Adjust if the popover goes off the right edge of the screen
  if (leftPosition + popoverRect.width >= windowWidth) {
    leftPosition = buttonRect.left - popoverRect.width;
  }
  // Adjust if the popover goes off the left edge of the screen
  if (leftPosition < 0) {
    leftPosition = 20;
  }

  filterStyles.value = {
    left: `${leftPosition}px`,
    opacity: "1",
  };
};
const countPages = ref<number>(1);

const detectHorizontalScroll = () => {
  columns.value.forEach((column) => {
    column.showFilter = false;
  });
};
const calculateColumnWidth = (width: number) => {
  return {
    process: width * 0.25,
    location: width * 0.4,
    date: width * 0.15,
    duration: width * 0.1,
    working_hours: width * 0.1,
  };
};

const anyOpenFilterComponent = computed(() => {
  return props.columns.find((column) => {
    return column.showFilter; // Make sure to return the result of column.showFilter
  });
});

const getFromRenderCache = (func: CallableFunction, value: unknown) => {
  const cached = renderCache.get(func)?.get(value);
  if (cached) {
    return cached;
  }
  const result = func(value);
  if (!renderCache.has(func)) {
    renderCache.set(func, new Map());
  }

  renderCache.get(func)?.set(value, result);
  return result;
};

const handleSort = (column: Column) => {
  lastSorted.value = column;
  column.sortActive = !column.sortActive;
  column.sortDirection = column.sortDirection === "desc" ? "asc" : "desc";
  const multiplier = column.sortDirection === "asc" ? 1 : -1;
  const sortFunction = column.toggleSort;
  if (sortFunction) {
    data.value.sort((a, b) => sortFunction(a[column.field], b[column.field]) * multiplier);
    return;
  }

  data.value.sort((a, b) => {
    const aValue = a[column.field];
    const bValue = b[column.field];

    if (typeof aValue === "string" && typeof bValue === "string") {
      return aValue.localeCompare(bValue) * multiplier;
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return (aValue - bValue) * multiplier;
    }

    if (isDate(aValue) && isDate(bValue)) {
      return ((aValue as Date).getTime() - (bValue as Date).getTime()) * multiplier;
    }
    return 0;
  });
};

const extractFilterSelectValues = (filterData: Data[]) => {
  const filterColumns = new Set(
    columns.value
      .filter((column) => column.filters?.some((f) => f.type === "process" || f.type === "select"))
      .map((column) => column.field),
  );
  const values = filterData.reduce((acc, item) => {
    Object.entries(item).forEach(([key, value]) => {
      if (filterColumns.has(key)) {
        if (key === "process") {
          acc[key] = acc[key] || new Set();
          const encodedLabel = item?.encoded_label?.toString();
          if (encodedLabel !== null) {
            acc[key].add(encodedLabel);
          }
        } else {
          acc[key] = acc[key] || new Set();
          acc[key].add(value);
        }
      }
    });
    return acc;
  }, {} as Record<string, Set<unknown>>);

  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      (Array.from(value) as string[])
        .sort((a, b) => a.localeCompare(b))
        .filter((v) => v)
        .map((v) => ({
          name: t(`process_classes.${v}`),
          value: v,
        })),
    ]),
  );
};

const filterStyles = ref<Record<string, string> | null>(null);

const toggleFilter = (column: Column, mode?: boolean) => {
  if (column.filterDisabled) {
    return;
  }

  column.showFilter = mode !== undefined ? mode : !column.showFilter;

  if (column.showFilter) {
    filterStyles.value = { opacity: "0" };
    setTimeout(() => {
      resizeFilterPopover(column);
    }, 100);
  }
};

const resetAllFilters = () => {
  filters.value = props.columns.reduce((acc, column) => {
    acc[column.field] = null;
    return acc;
  }, {} as Record<string, null>);

  columns.value.forEach((column) => {
    if (column.filterValue) {
      column.filterValue = undefined;
    }

    column.filters?.forEach((filter) => {
      if (column.field === "location") {
        filter.filterValue = [];
      } else if (column.field === "process") {
        filter.filterValue = { processes: [], processTypes: [] } as Record<
          "processes" | "processTypes",
          string[]
        >;
      } else if (column.field === "process_ids") {
        if (filter.filterValue) {
          (filter.filterValue as unknown) = [];
        }
      } else {
        if (filter.filterValue) {
          (filter.filterValue as unknown) = undefined;
        }
      }
    });
    column.showFilter = false;
  });

  dateRange.value = null;
  data.value = props.data;
  setTimeout(() => {
    router.replace({
      query: {
        ...route.query,
        processes: undefined,
        processTypes: undefined,
        location: undefined,
        process_ids: undefined,
        processId: undefined,
      },
    });
  }, 100);
  emits("update:data", data.value);
};

const filterAllColumns = () => {
  let dataToFilter = props.data;

  if (dateRange.value) {
    dataToFilter = props.onDateFilter?.(dateRange.value, dataToFilter) || dataToFilter;
  }

  Object.entries(filters.value).forEach(([key, value]) => {
    if (!value) {
      return;
    }

    const isMatchingLocation =
      key === "location"
        ? createIsMatchingLocationFn(value as string[], props.hierarchyTags)
        : undefined;

    dataToFilter = dataToFilter.filter((item) => {
      const column = columns.value.find((column) => column.field === key);

      if (!column) {
        return true;
      }

      if (column.filters?.length) {
        return column.filters?.every((filter) => {
          if (filter.filterValue === undefined) {
            return true;
          }
          if (filter.type === "location" && isMatchingLocation) {
            return isMatchingLocation(item.tagIds as Record<HierarchyType, string>);
          }
          return filter.filterFn(filter.filterValue, item, column);
        });
      }
      return stringFilterFunction(item, column);
    });
  });

  data.value = dataToFilter;
  emits("update:data", data.value);
};

const checkEmptyProcessFilter = computed(() => {
  const processes = filters?.value.process as null | Record<"processTypes" | "processes", string[]>;
  if (processes) {
    return processes.processes.length === 0;
  }

  return true; // Default to true if processes is null or undefined
});

const trackEvent = useTrackEvent();

const handleCustomFilter = (value: Filter["filterValue"], column: Column) => {
  const filter = column.filters?.find(Boolean);
  if (filter) {
    filter.filterValue = value;
  }

  filters.value = {
    ...filters.value,
    [column.field]: value,
  };
  trackEvent("activity-log_filter_apply", { type: column.field });
  filterAllColumns();
};

watch(
  () => filters.value,
  () => {
    const processColFilters = filters.value.process as Record<string, string[]>;
    const locationColFilters = filters.value.location as string[];
    const dateColFilters = filters.value.date as unknown as Date[];

    if (processColFilters || locationColFilters || dateColFilters) {
      router.replace({
        query: {
          ...route.query,
          processes: processColFilters?.processes.length
            ? processColFilters?.processes.join(",")
            : undefined,
          processTypes: processColFilters?.processTypes.length
            ? processColFilters?.processTypes.join(",")
            : undefined,
          location: locationColFilters?.length ? locationColFilters.join(",") : undefined,
          date: dateColFilters?.length
            ? dateColFilters.map((item: Date) => format(item, "yyyy-MM-dd")).join(",")
            : undefined,
        },
      });
    }
    filterAllColumns();
  },
  { deep: true },
);

const stringFilterFunction = (item: Data, column: Column) => {
  if (!filters.value[column.field]) {
    return true;
  }
  const value = String(item[column.field]).toLowerCase();
  const filterValue = String(filters.value[column.field]).toLowerCase();
  return value.includes(filterValue);
};

const handleFilter = (value: string, column: Column) => {
  filters.value = {
    ...filters.value,
    [column.field]: value,
  };
  filterAllColumns();
};

const hasWorkingHoursFeature = useHasProjectFeature("working_hours");

const hideWorkingHours = (column: Column) => {
  return column.field === "working_hours" && !hasWorkingHoursFeature;
};

const processIdMap = processClasses.value.reduce((acc, item) => {
  if (item.encodedLabel !== undefined && item.processElement !== undefined) {
    acc[item.encodedLabel] = item;
  }
  return acc;
}, {} as Record<string, ProcessClass>);

const handleProcessFilter = (
  currentValue: Record<"processTypes" | "processes", string[]>,
  initialValue: string | string[] | Record<string, string[] | Date[]>,
  column: Column,
) => {
  const prevValue = initialValue as Record<"processes" | "processTypes", string[]>;
  if (currentValue.processes.length !== prevValue.processes.length) {
    const prevProcessesSet = new Set(prevValue.processes);
    const currentProcessesSet = new Set(currentValue.processes);
    // add filter
    if (currentValue.processes.length > prevValue.processes.length) {
      const diff = currentValue.processes.find((item) => !prevProcessesSet.has(item));
      if (diff) {
        const processElement = processIdMap[diff].processElement;
        if (!currentValue.processTypes.includes(processElement)) {
          currentValue.processTypes.push(processElement);
        }
      }
    } else {
      const diff = prevValue.processes.find((item) => !currentProcessesSet.has(item));
      if (diff !== undefined) {
        const processElement = processIdMap[diff].processElement;
        const allItemsUnselected = processClasses.value
          .filter(
            (item) =>
              item.processElement === processElement &&
              prevProcessesSet.has(item.encodedLabel.toString()),
          )
          .some((item) => item.encodedLabel.toString() !== diff);

        if (!allItemsUnselected) {
          (currentValue.processTypes as string[]).splice(
            (currentValue.processTypes as string[]).indexOf(processElement),
            1,
          );
        }
      }
    }
  } else if (currentValue.processTypes.length !== prevValue.processTypes.length) {
    // add filter
    if (currentValue.processTypes.length > prevValue.processTypes.length) {
      const diff = currentValue.processTypes.find((item) => !prevValue.processTypes.includes(item));
      const processList: string[] = [];
      props.data?.forEach((item) => {
        if (!processList.includes(item?.encoded_label as string))
          processList.push(item?.encoded_label as string);
      });
      processClasses.value.forEach((item) => {
        if (item.processElement === diff) {
          if (processList.includes(item?.encodedLabel as unknown as string)) {
            (currentValue.processes as string[]).push(item.encodedLabel.toString());
          }
        }
      });
      // remove filter
    } else {
      const mappedProcessTypes = new Set(currentValue.processTypes);
      const diff = prevValue.processTypes.find((item) => !mappedProcessTypes.has(item));
      currentValue.processes = (currentValue.processes as string[]).filter(
        (item) => processIdMap[item].processElement !== diff,
      );
    }
  }
  handleCustomFilter(currentValue, column);
};
const handleDebouncedFilter = debounce(handleFilter, 300);
const handleDebouncedCustomFilter = debounce(handleCustomFilter, 300);

const activeFilters = computed(() => {
  return (
    ((filters?.value.location || []) as string[]).length > 0 ||
    dateRange.value || // Assuming dateRange is a ref
    Object.entries(filters.value).some(
      ([key, value]) =>
        !["location", "process"].includes(key) && Array.isArray(value) && value.length > 0,
    ) ||
    !checkEmptyProcessFilter.value
  );
});

const anyFilterOpen = computed(() => {
  return columns.value.some((item) => item.showFilter);
});

const removeFilter = (column: Column) => {
  column.filters?.forEach((filter) => {
    if (column.field === "location") {
      filter.filterValue = [];
    } else if (column.field === "process") {
      filter.filterValue = { processes: [], processTypes: [] } as Record<
        "processes" | "processTypes",
        string[]
      >;
    } else {
      if (filter.filterValue) {
        (filter.filterValue as unknown) = undefined;
      }
    }
    handleDebouncedCustomFilter(filter.filterValue, column);
  });
  column.showFilter = false;

  emits("update:data", data.value);
};

const columnTotal = () => Object.values(colWidth.value).reduce((a, b) => a + b, 0);

const handleProcessSliderMoved = (value: TableResizeSliderEvent) => {
  if (value.x > minColumnWidth) {
    const newProcessWidth = value.x;
    if (newProcessWidth > minColumnWidth && newProcessWidth < props.width) {
      colWidth.value.process = newProcessWidth;
    }
    resetLastColumn();
  }
};

const handleLocationSliderMoved = (value: TableResizeSliderEvent) => {
  const newLocationWidth = value.x - colWidth.value.process;

  if (newLocationWidth > minColumnWidth && newLocationWidth < props.width) {
    colWidth.value.location = newLocationWidth;
  }
  resetLastColumn();
};

const handleDateSliderMoved = (value: TableResizeSliderEvent) => {
  const newDateWidth = value.x - colWidth.value.location - colWidth.value.process;

  if (newDateWidth > minColumnWidth && newDateWidth < props.width) {
    colWidth.value.date = newDateWidth;
  }
  resetLastColumn();
};

const handleDurationSliderMoved = (value: TableResizeSliderEvent) => {
  const newDurationWidth =
    value.x - colWidth.value.date - colWidth.value.location - colWidth.value.process;

  if (newDurationWidth > minColumnWidth && newDurationWidth < props.width) {
    colWidth.value.duration = newDurationWidth;
  }

  resetLastColumn();
};

const resetLastColumn = () => {
  const total = columnTotal();
  if (total < props.width) {
    colWidth.value.working_hours = props.width - (total - colWidth.value.working_hours);
  }
  if (total > props.width && colWidth.value.working_hours > minColumnWidth) {
    colWidth.value.working_hours = total - colWidth.value.working_hours - props.width;
  }
};

const hasChangeOfKw = (value: unknown, index: number) => {
  const date = value as Date;
  return (
    (index === 0 && lastSorted.value?.field === "date") ||
    (lastSorted.value?.field === "date" &&
      data.value[index - 1] &&
      value &&
      getWeek(date) !== getWeek(data.value[index - 1].date as Date))
  );
};

onMounted(() => {
  nextTick(() => {
    tableInitialWidth.value = props.width;
    colWidth.value = calculateColumnWidth(props.width);
    initialLastCols.value = colWidth.value.working_hours;
  });
  window.addEventListener("resize", handleWindowResize);
  filterAllColumns();
});

onUnmounted(() => {
  window.removeEventListener("resize", handleWindowResize);
});

__expose({
  resetAllFilters: resetAllFilters,
  activeFilters: activeFilters,
  tableWidth: tableWidth.value,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _renderSlot(_ctx.$slots, "header"),
    _createElementVNode("div", {
      class: "shadow flex flex-col max-h-full overflow-x-scroll h-full rounded-lg",
      onScroll: detectHorizontalScroll
    }, [
      _createElementVNode("div", {
        class: "pointer-events-none z-10 sticky w-max top-0",
        style: _normalizeStyle({ minWidth: __props.width + 'px' })
      }, [
        _createElementVNode("ul", _hoisted_2, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (column, index) => {
            return (_openBlock(), _createElementBlock("li", {
              ref_for: true,
              ref: 'h' + column.field,
              key: index,
              class: _normalizeClass([
              `p-3 text-left text-xs font-medium  w-max text-gray-500 border-b border-gray-200`,
              lastSorted.value === column && 'border-yellow-500',
              column.class,
            ]),
              style: _normalizeStyle({
                width: colWidth.value[column.field as ColNames] + 'px',
                flex: '0 0 ' + colWidth.value[column.field as ColNames] + 'px',
              })
            }, [
              _createElementVNode("div", _hoisted_3, [
                (hideWorkingHours(column))
                  ? (_openBlock(), _createBlock(OaiTooltip, {
                      key: 0,
                      position: "right"
                    }, {
                      tooltip: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(t)("paywall.working_hours")), 1)
                      ]),
                      default: _withCtx(() => [
                        _createVNode(_unref(LockClosedIcon), { class: "h-4 w-4" })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(OaiTooltip, { simple: "" }, {
                  tooltip: _withCtx(() => [
                    _createElementVNode("div", _hoisted_7, _toDisplayString(`${column.label}\n${column?.tooltip || ""}`), 1)
                  ]),
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("span", _hoisted_6, _toDisplayString(column.label), 1)
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createElementVNode("div", {
                  class: _normalizeClass([
                  'flex items-center justify-end gap-2 relative',
                  { 'h-4': !column.filterDisabled && column.field !== 'column.field' },
                ])
                }, [
                  (!column.filterDisabled && column.field !== 'process_ids')
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        id: `filterContainer_${column.field}`
                      }, [
                        _createElementVNode("button", {
                          onClick: ($event: any) => (toggleFilter(column)),
                          type: "button",
                          tabindex: column.field,
                          class: "relative",
                          id: `filterButton_${column.field}`
                        }, [
                          _createElementVNode("span", _hoisted_10, [
                            (
                          !filters.value[column.field] ||
                          (Array.isArray(filters.value[column.field]) &&
                            !filters.value[column.field]?.length) ||
                          (checkEmptyProcessFilter.value && column.field === 'process')
                        )
                              ? (_openBlock(), _createBlock(_unref(FunnelIcon), {
                                  key: 0,
                                  class: "m-0 block h-4 w-4"
                                }))
                              : (_openBlock(), _createElementBlock("span", _hoisted_11, [
                                  _createElementVNode("span", {
                                    onClick: _withModifiers(($event: any) => (removeFilter(column)), ["stop"]),
                                    class: "absolute top-0 right-full text-gray-500 text-xs hover:underline bg-gray-50 px-1.5"
                                  }, _toDisplayString(_ctx.$t("buttons.clear")), 9, _hoisted_12),
                                  _createVNode(_unref(FunnelIconSolid), { class: "m-0 h-4 w-4 text-yellow-400" })
                                ]))
                          ])
                        ], 8, _hoisted_9),
                        (column.showFilter)
                          ? (_openBlock(), _createBlock(_unref(OnClickOutside), {
                              key: 0,
                              options: {
                      ignore: [`#filterButton_${column.field}`],
                    },
                              onTrigger: ($event: any) => (toggleFilter(column, false))
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", {
                                  onMouseover: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
                                  class: "absolute top-[100%] z-20 mt-2 min-w-[14rem] rounded-md bg-white shadow-lg"
                                }, [
                                  (!column.filters?.length)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                        _withDirectives(_createElementVNode("input", {
                                          "onUpdate:modelValue": ($event: any) => ((column.filterValue) = $event),
                                          type: "text",
                                          class: "block rounded-md border-gray-300 shadow-sm focus:ring-0 p-3 py-2 text-sm",
                                          placeholder: "Filter",
                                          onInput: ($event: any) => (
                            _unref(handleDebouncedFilter)(($event.target as HTMLInputElement).value, column)
                          ),
                                          onClick: _cache[0] || (_cache[0] = ($event: any) => ($event.stopPropagation()))
                                        }, null, 40, _hoisted_14), [
                                          [_vModelText, column.filterValue]
                                        ])
                                      ]))
                                    : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(column.filters, (filter, i) => {
                                        return (_openBlock(), _createElementBlock(_Fragment, { key: i }, [
                                          (filter.type === 'text')
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                                _withDirectives(_createElementVNode("input", {
                                                  "onUpdate:modelValue": ($event: any) => ((filter.filterValue) = $event),
                                                  type: "text",
                                                  class: "block rounded-md border-gray-300 shadow-sm focus:ring-0 px-3 py-2 text-sm",
                                                  placeholder: filter.placeholder,
                                                  onInput: ($event: any) => (
                              _unref(handleDebouncedCustomFilter)(
                                ($event.target as HTMLInputElement).value,
                                column,
                              )
                            )
                                                }, null, 40, _hoisted_16), [
                                                  [_vModelText, filter.filterValue]
                                                ])
                                              ]))
                                            : _createCommentVNode("", true),
                                          (filter.type === 'date')
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 1,
                                                class: "fixed",
                                                style: _normalizeStyle(filterStyles.value)
                                              }, [
                                                _createVNode(_unref(VueDatePicker), {
                                                  modelValue: filter.filterValue as [Date, Date],
                                                  "onUpdate:modelValue": [($event: any) => ((filter.filterValue as [Date, Date]) = $event), ($event: any) => (_unref(handleDebouncedCustomFilter)($event, column))],
                                                  columns: 6,
                                                  "enable-time-picker": false,
                                                  placeholder: `${_unref(t)('report.bulk_export.date_range_placeholder')}`,
                                                  locale: _unref(locale),
                                                  format: "dd.MM.yyyy",
                                                  "auto-apply": "",
                                                  range: "",
                                                  static: "",
                                                  inline: "",
                                                  class: "PopoverPanelContent"
                                                }, null, 8, ["modelValue", "onUpdate:modelValue", "placeholder", "locale"])
                                              ], 4))
                                            : _createCommentVNode("", true),
                                          (filter.type === 'process')
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 2,
                                                class: "fixed px-4",
                                                style: _normalizeStyle(filterStyles.value),
                                                onMouseover: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                                              }, [
                                                _createVNode(ProcessTypesFilter, {
                                                  multiple: "",
                                                  "onUpdate:selected": ($event: any) => (
                              handleProcessFilter(
                                $event,
                                filter.filterValue as string[],
                                column as Column,
                              )
                            ),
                                                  selected: filter.filterValue as (typeof ProcessTypesFilter)['selected'],
                                                  groups: [
                              {
                                name: _unref(t)('analytics.processes.process_group'),
                                field: 'processTypes',
                                options: processTypes.value
                                  .filter((type) => type)
                                  .map((type) => ({
                                    value: type,
                                    name: _unref(t)(`process_classes.${type}`),
                                  })),
                              },
                              {
                                name: _unref(t)('analytics.processes.processes'),
                                field: 'processes',
                                options: filterSelectValues.value[column.field].sort((a, b) =>
                                  a.name.localeCompare(b.name),
                                ),
                              },
                            ],
                                                  dependencies: {
                                        processTypes: {
                                          to: 'processes',
                                          defineDependencies: (options, value) => {
                                            return options.filter((item) => {
                                              return _unref(processIdMap)[item]?.processElement === value;
                                            });
                                          },
                                        },
                                  },
                                                  placeholder: "analytics.processes.placeholder_process_type",
                                                  defaultOpen: ""
                                                }, null, 8, ["onUpdate:selected", "selected", "groups", "dependencies"])
                                              ], 36))
                                            : _createCommentVNode("", true),
                                          (filter.type === 'location')
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 3,
                                                class: "fixed px-4",
                                                style: _normalizeStyle(filterStyles.value)
                                              }, [
                                                _createVNode(LocationFilter, {
                                                  class: "mt-2 lg:mt-0 text-left",
                                                  onChange: ($event: any) => (_unref(handleDebouncedCustomFilter)($event, column)),
                                                  selected: filter.filterValue as string[],
                                                  hierarchyTags: __props.hierarchyTags,
                                                  placeholder: _unref(t)('analytics.processes.placeholder_hierarchy_tag'),
                                                  defaultOpen: "",
                                                  useNameAsId: true
                                                }, null, 8, ["onChange", "selected", "hierarchyTags", "placeholder"])
                                              ], 4))
                                            : _createCommentVNode("", true)
                                        ], 64))
                                      }), 128))
                                ], 32)
                              ]),
                              _: 2
                            }, 1032, ["options", "onTrigger"]))
                          : _createCommentVNode("", true)
                      ], 8, _hoisted_8))
                    : _createCommentVNode("", true),
                  (!column.sortingDisabled && !hideWorkingHours(column))
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        type: "button",
                        class: _normalizeClass([
                    'text-gray-400 hover:text-yellow-500 leading-none self-center',
                    lastSorted.value === column && 'text-yellow-500',
                  ]),
                        onClick: ($event: any) => {
                    handleSort(column);
                    _unref(trackEvent)('activity-log_sort_apply', { column: column?.field });
                  }
                      }, [
                        _createVNode(_unref(ChevronDownIcon), {
                          class: _normalizeClass([
                      'm-0 block w-4 transition ease-in-out duration-150 transform rotate-0',
                      column.sortDirection === 'desc' && 'rotate-180',
                    ])
                        }, null, 8, ["class"])
                      ], 10, _hoisted_17))
                    : _createCommentVNode("", true)
                ], 2)
              ])
            ], 6))
          }), 128))
        ])
      ], 4),
      _createElementVNode("div", {
        class: "bg-white h-full overflow-y-auto relative overflow-x-hidden flex-1 w-max mt-[-45px]",
        onScroll: detectTableScroll,
        ref_key: "tableContent",
        ref: tableContent
      }, [
        _hoisted_18,
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayedData.value, (row, rowIndex) => {
          return (_openBlock(), _createElementBlock("div", {
            key: rowIndex,
            style: _normalizeStyle({ minWidth: __props.width + 'px' })
          }, [
            _createElementVNode("ul", {
              class: _normalizeClass(['flex items-stretch', row.expanded && 'bg-gray-100'])
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (column, columnIndex) => {
                return (_openBlock(), _createElementBlock("li", {
                  class: _normalizeClass([
                `px-3 py-2 whitespace-nowrap border-t relative w-max border-solid border-t-gray-200 group-hover:bg-yellow-200`,
                (__props.onRowClick || __props.onRowDbClick) && 'cursor-pointer',
                row.class,
                column.field === 'process_ids' && 'hidden',
              ]),
                  style: _normalizeStyle({
                width: colWidth.value[column.field as ColNames] + 'px',
                flex: '0 0 ' + colWidth.value[column.field as ColNames] + 'px',
                minHeight: '45px'
              }),
                  onClick: ($event: any) => (__props.onRowClick?.(row)),
                  onDblclick: ($event: any) => (__props.onRowDbClick?.(row)),
                  key: columnIndex,
                  id: row?._id ? `${row._id}` : undefined
                }, [
                  (column.field === 'process' && hasChangeOfKw(row['date'], rowIndex))
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: _normalizeClass([
                  'absolute bg-orange-600  -ml-3 py-0.5 px-2.5 text-white rounded-full text-xs',
                  { 'z-10': rowIndex === 0 && !tableIsScrolling.value && !anyFilterOpen.value },
                ]),
                        style: {"top":"-11px"}
                      }, [
                        _createElementVNode("small", null, _toDisplayString(_unref(t)("analytics.planner.calendar_week_label")) + " " + _toDisplayString(_unref(getWeek)(row["date"] as Date)), 1)
                      ], 2))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_20, [
                    _createElementVNode("div", _hoisted_21, [
                      (column.renderComponent)
                        ? (_openBlock(), _createBlock(_resolveDynamicComponent(column.renderComponent), {
                            key: 0,
                            data: row[column.field],
                            field: column.field
                          }, null, 8, ["data", "field"]))
                        : (column.render)
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 1,
                              style: _normalizeStyle({
                      filter: hideWorkingHours(column) ? 'blur(3px)' : '',
                      'user-select': hideWorkingHours(column) ? 'none' : 'auto',
                    })
                            }, _toDisplayString(getFromRenderCache(column.render, row[column.field])), 5))
                          : (_openBlock(), _createElementBlock("span", _hoisted_22, _toDisplayString(row[column.field]), 1))
                    ])
                  ])
                ], 46, _hoisted_19))
              }), 128))
            ], 2)
          ], 4))
        }), 128)),
        (countPages.value * 100 < data.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
              _createElementVNode("button", {
                onClick: _cache[3] || (_cache[3] = ($event: any) => (countPages.value++)),
                class: "flex text-xs items-center hover:text-white hover:bg-orange bg-gray-100 px-3 py-1 rounded-md leading-none border"
              }, [
                _createVNode(_unref(ArrowDownIcon), { class: "h-3 pr-1" }),
                _createTextVNode(_toDisplayString(_ctx.$t("buttons.load_more")), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        (!anyOpenFilterComponent.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (isThisSliderDraggingOrNone('process'))
                ? (_openBlock(), _createBlock(TableResizeSlider, {
                    key: 0,
                    width: sliderWidth,
                    containerRef: getContainerRef,
                    currentX: colWidth.value.process,
                    onMoved: handleProcessSliderMoved,
                    onStarted: _cache[4] || (_cache[4] = ($event: any) => (draggingSliderName.value = 'process')),
                    onEnded: _cache[5] || (_cache[5] = ($event: any) => (draggingSliderName.value = null)),
                    tableContentHeight: tableContent.value?.scrollHeight
                  }, null, 8, ["currentX", "tableContentHeight"]))
                : _createCommentVNode("", true),
              (isThisSliderDraggingOrNone('location'))
                ? (_openBlock(), _createBlock(TableResizeSlider, {
                    key: 1,
                    width: sliderWidth,
                    containerRef: getContainerRef,
                    currentX: colWidth.value.process + colWidth.value.location,
                    onMoved: handleLocationSliderMoved,
                    onStarted: _cache[6] || (_cache[6] = ($event: any) => (draggingSliderName.value = 'location')),
                    onEnded: _cache[7] || (_cache[7] = ($event: any) => (draggingSliderName.value = null)),
                    tableContentHeight: tableContent.value?.scrollHeight
                  }, null, 8, ["currentX", "tableContentHeight"]))
                : _createCommentVNode("", true),
              (isThisSliderDraggingOrNone('date'))
                ? (_openBlock(), _createBlock(TableResizeSlider, {
                    key: 2,
                    width: sliderWidth,
                    containerRef: getContainerRef,
                    currentX: colWidth.value.process + colWidth.value.location + colWidth.value.date,
                    onMoved: handleDateSliderMoved,
                    onStarted: _cache[8] || (_cache[8] = ($event: any) => (draggingSliderName.value = 'date')),
                    onEnded: _cache[9] || (_cache[9] = ($event: any) => (draggingSliderName.value = null)),
                    tableContentHeight: tableContent.value?.scrollHeight
                  }, null, 8, ["currentX", "tableContentHeight"]))
                : _createCommentVNode("", true),
              (isThisSliderDraggingOrNone('duration'))
                ? (_openBlock(), _createBlock(TableResizeSlider, {
                    key: 3,
                    width: sliderWidth,
                    containerRef: getContainerRef,
                    currentX: colWidth.value.process + colWidth.value.location + colWidth.value.date + colWidth.value.duration,
                    onMoved: handleDurationSliderMoved,
                    onStarted: _cache[10] || (_cache[10] = ($event: any) => (draggingSliderName.value = 'duration')),
                    onEnded: _cache[11] || (_cache[11] = ($event: any) => (draggingSliderName.value = null)),
                    tableContentHeight: tableContent.value?.scrollHeight
                  }, null, 8, ["currentX", "tableContentHeight"]))
                : _createCommentVNode("", true)
            ], 64))
          : _createCommentVNode("", true)
      ], 544),
      (__props.summary)
        ? (_openBlock(), _createElementBlock("ul", {
            key: 0,
            class: "border-t-2 flex justify-between w-min text-sm h-6 bg-gray-50",
            style: _normalizeStyle({ minWidth: __props.width + 'px' })
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (column, index) => {
              return (_openBlock(), _createElementBlock("li", {
                key: index,
                class: _normalizeClass([
            'py-1 px-3 text-left text-xs font-medium justify-self-start text-gray-500 overflow-hidden truncate',
            { hidden: column.field === 'process_ids' },
          ]),
                style: _normalizeStyle({
            width: colWidth.value[column.field as ColNames] + 'px',
            flex: '0 0 ' + colWidth.value[column.field as ColNames] + 'px',
          })
              }, [
                (__props.summary[column.field as ColNames])
                  ? (_openBlock(), _createElementBlock("span", _hoisted_24, [
                      _createElementVNode("span", {
                        style: _normalizeStyle({
                filter: hideWorkingHours(column) ? 'blur(3px)' : '',
                'user-select': hideWorkingHours(column) ? 'none' : 'auto',
              })
                      }, _toDisplayString(!hideWorkingHours(column)
                  ? __props.summary[column.field as ColNames]
                  : `Σ 000.00 ${_unref(t)("time.hour", { count: 2 })}`), 5)
                    ]))
                  : _createCommentVNode("", true)
              ], 6))
            }), 128))
          ], 4))
        : _createCommentVNode("", true)
    ], 32)
  ]))
}
}

})